import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { User, Upload, Camera, Save } from "lucide-react";
import StateCitySelect from "@/components/StateCitySelect";
import PinCodeLookup from "@/components/PinCodeLookup";
import { OTHER_CITY, citiesFor } from "@/constants/cities";

export default function DistProfile() {
  const { user, refresh } = useAuth();
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ext, setExt] = useState({ full_address: "", dob: "", father_name: "", qualification: "", state: "", city: "", city_other: "", pin_code: "" });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/dist/profile-photo").then((r) => setPhoto(r.data?.url || null)).catch(() => {});
    api.get("/dist/profile-extended").then((r) => {
      // Normalise the persisted city into either a dropdown value or the
      // "Other" free-text fallback so the picker mirrors what the user saved.
      const known = citiesFor(r.data?.state || "");
      const saved = (r.data?.city || "").trim();
      const inList = saved && known.some((c) => c.toLowerCase() === saved.toLowerCase());
      setExt((prev) => ({
        ...prev,
        ...r.data,
        city: saved ? (inList ? known.find((c) => c.toLowerCase() === saved.toLowerCase()) : OTHER_CITY) : "",
        city_other: saved && !inList ? saved : "",
      }));
    }).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/dist/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(r.data?.url);
      toast.success("Profile photo updated");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const saveExt = async (e) => {
    e.preventDefault();
    if (ext.dob) {
      const d = new Date(ext.dob), t = new Date();
      let age = t.getFullYear() - d.getFullYear();
      if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) age--;
      if (age < 18) return toast.error(`You must be at least 18 (age ${age})`);
    }
    setSaving(true);
    try {
      const city_final = ext.city === OTHER_CITY ? (ext.city_other || "").trim() : (ext.city || "").trim();
      const { city_other, ...rest } = ext;  // eslint-disable-line no-unused-vars
      await api.patch("/dist/profile-extended", { ...rest, city: city_final });
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="My Profile" subtitle="Personal details & profile photo">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Photo */}
        <div className="bg-white border border-line p-6 rounded-sm h-fit">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-36 h-36 rounded-full bg-gradient-to-br from-gold/20 to-ivory border-2 border-gold/30 grid place-items-center overflow-hidden shadow-inner">
                {photo ? <img src={photo} alt="" data-testid="profile-photo-preview" className="w-full h-full object-cover" />
                       : <User size={64} className="text-navy/40" />}
              </div>
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="profile-photo-camera" className="absolute bottom-1 right-1 w-10 h-10 rounded-full bg-royal text-white grid place-items-center shadow-lg hover:bg-royal-deep">
                <Camera size={16} />
              </button>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-gold-deep">{user?.user_code}</div>
              <div className="font-serif text-xl text-royal mt-1">{user?.name}</div>
              <div className="text-sm text-navy/60 mt-0.5">{user?.email}</div>
            </div>
            <input ref={fileRef} type="file" hidden accept="image/*" onChange={(e) => upload(e.target.files?.[0])} data-testid="profile-photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-photo-upload-btn" className="bg-royal text-white px-5 py-2.5 hover:bg-royal-deep rounded-sm text-sm disabled:opacity-50 inline-flex items-center gap-2">
              <Upload size={14} /> {uploading ? "Uploading..." : photo ? "Change" : "Upload"}
            </button>
            <div className="text-[10px] uppercase tracking-widest text-navy/50 text-center">JPG or PNG · max 5MB</div>
          </div>
        </div>

        {/* Extended profile */}
        <form onSubmit={saveExt} className="lg:col-span-2 bg-white border border-line p-6 rounded-sm space-y-4">
          <h3 className="font-serif text-xl text-navy">Personal Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Father's Name" tid="prof-father" value={ext.father_name}
               onChange={(v) => setExt({ ...ext, father_name: v })} />
            <F label="Date of Birth" tid="prof-dob" type="date" value={ext.dob}
               onChange={(v) => setExt({ ...ext, dob: v })} />
            <F label="Qualification" tid="prof-qualification" value={ext.qualification}
               onChange={(v) => setExt({ ...ext, qualification: v })} />
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-widest text-navy/60">Full Address</label>
              <textarea data-testid="prof-address" rows={3} value={ext.full_address}
                onChange={(e) => setExt({ ...ext, full_address: e.target.value })}
                placeholder="House / Street / Landmark, Pin Code"
                className="mt-1 w-full border border-border px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none resize-none" />
            </div>
            <div className="md:col-span-2">
              <PinCodeLookup
                pin={ext.pin_code}
                onChange={(v) => setExt({ ...ext, pin_code: v })}
                onResolved={(r) => setExt((prev) => ({
                  ...prev,
                  state:      r.state,
                  city:       r.city,
                  city_other: r.cityOther,
                }))}
                testidPrefix="prof-pin"
              />
            </div>
            <div className="md:col-span-2">
              <StateCitySelect
                state={ext.state}
                city={ext.city}
                cityOther={ext.city_other}
                onChange={(v) => setExt({ ...ext, ...v })}
                testidPrefix="prof"
              />
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="prof-save" className="bg-royal text-white px-5 py-2.5 hover:bg-royal-deep rounded-sm text-sm disabled:opacity-50 inline-flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving..." : "Save changes"}
          </button>
          <div className="text-[11px] text-navy/50 leading-relaxed pt-2 border-t border-line/70">
            💡 <strong>Tip:</strong> These details auto-fill your KYC address section — update them here once and reuse across every form.
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

function F({ label, value, onChange, type = "text", tid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-navy/60">{label}</label>
      <input data-testid={tid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-border px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none" />
    </div>
  );
}
